/* ─────────────────────────────────────────────────────────────────
   OPLYX — Landing (Iter 117 — light palette + hierarquia de diferenciais)

   Paleta (clara/creme, premium discreta):
     canvas       #f5f4ee   (creme quente, base)
     elevated     #ffffff
     dark surface #0d0d0d   (mocks técnicos, painéis de contraste)
     ink primary  #0a0a0a
     ink secondary#52524a
     ink muted    #8c8c82
     border       #e5e3d8
     accent green #2A8A58   (CTAs, checks)
     accent dark  #185c3a
     accent copper#b66a32   (números, destaques quentes)

   Tipografia:
     Display/serif: Fraunces (Google, carregada)
     Sans:          Inter
     Mono:          font-mono

   Hierarquia de diferenciais (founder):
     1. OPLYX Intelligence   2. Recusteio automático   3. Caixa offline
     4. DRE em tempo real    5. Fiscal nativo  6. Registro de Ponto
     7. Recusteio automático   8. Multi-filial
   ───────────────────────────────────────────────────────────────── */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { planLandingBullets } from "@/lib/feature_catalog";
import { COMPANY, companyAddress } from "@/brand";
import { openCookiePreferences } from "@/lib/consent";

// ─── Hook: revela ao scrollar ──────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setVisible(true)),
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, visible];
}

const Reveal = ({ children, delay = 0, className = "", immediate = false }) => {
  const [ref, visible] = useReveal();
  const show = visible || immediate;
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out ${className}`}
      style={{
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(28px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// ─── Counter animado ───────────────────────────────────────────────
function Counter({ to, prefix = "", suffix = "", duration = 1600 }) {
  const [ref, visible] = useReveal();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!visible) return;
    const t0 = performance.now();
    let frame;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const v = 1 - Math.pow(1 - p, 3);
      setN(to * v);
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [visible, to, duration]);
  const fmt = (x) =>
    x >= 1000 ? Math.round(x).toLocaleString("pt-BR") : x.toFixed(x < 10 ? 1 : 0);
  return (
    <span ref={ref}>
      {prefix}{fmt(n)}{suffix}
    </span>
  );
}

// ─── Top Nav ───────────────────────────────────────────────────────
function TopNav({ onCta }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#f5f4ee]/85 backdrop-blur-xl border-b border-[#e5e3d8]" : "bg-transparent"
      }`}
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <a href="#top" className="flex items-center gap-3" data-testid="landing-logo">
          <img src="/oplyx-header-cream.png" alt="OPLYX" height={28} style={{ height: 28, width: "auto", objectFit: "contain" }} />
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#23784c]">foodservice OS</span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm text-[#52524a]">
          <a href="#recusteio" className="hover:text-[#0a0a0a] transition-colors">Recusteio</a>
          <a href="#manifesto" className="hover:text-[#0a0a0a] transition-colors">Manifesto</a>
          <a href="#pilares" className="hover:text-[#0a0a0a] transition-colors">Como funciona</a>
          <a href="#diferenciais" className="hover:text-[#0a0a0a] transition-colors">Comparativo</a>
          <a href="#precos" className="hover:text-[#0a0a0a] transition-colors">Planos</a>
          <a href="#faq" className="hover:text-[#0a0a0a] transition-colors">FAQ</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/auth"
            onClick={(e) => {
              if (typeof window !== "undefined") {
                const h = (window.location.hostname || "").toLowerCase();
                if (h === "oplyx.com.br" || h === "www.oplyx.com.br") {
                  e.preventDefault();
                  window.location.href = "https://app.oplyx.com.br/auth";
                }
              }
            }}
            className="hidden sm:inline-block text-sm text-[#52524a] hover:text-[#0a0a0a] transition-colors"
            data-testid="landing-nav-login"
          >
            Entrar
          </Link>
          <button
            onClick={onCta}
            className="text-sm bg-[#23784c] hover:bg-[#185c3a] text-white font-medium px-4 py-2 rounded-full transition-colors"
            data-testid="landing-nav-cta"
          >
            Contratar
          </button>
        </div>
      </div>
    </header>
  );
}

// ─── Mock: DRE em tempo real ───────────────────────────────────────
function DreMock() {
  return (
    <div
      className="bg-[#121212] border border-white/[0.08] rounded-2xl p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
      data-testid="hero-dre-mock"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#c87a3f] font-semibold">DRE em tempo real</div>
          <div className="text-[#f1f1ef] font-[Fraunces] text-2xl tracking-tight mt-1">Junho 2026</div>
        </div>
        <span className="text-[10px] text-[#8b8b94] uppercase tracking-wider flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /> ao vivo
        </span>
      </div>
      <div className="space-y-2 font-mono text-[13px]">
        {[
          ["Receita bruta", "R$ 524.183", "text-[#f1f1ef]"],
          ["CMV (com fator)", "− R$ 142.811", "text-[#a1a1aa]"],
          ["CMO (folha + encargos)", "− R$ 89.420", "text-[#a1a1aa]"],
          ["Taxas cartão + maquininha", "− R$ 18.346", "text-[#a1a1aa]"],
          ["Despesas operacionais", "− R$ 87.910", "text-[#a1a1aa]"],
        ].map(([k, v, cls]) => (
          <div key={k} className="flex justify-between border-b border-white/[0.04] py-1.5">
            <span className="text-[#8b8b94] tracking-tight">{k}</span>
            <span className={cls}>{v}</span>
          </div>
        ))}
        <div className="flex justify-between pt-3">
          <span className="text-[#f1f1ef] font-semibold tracking-tight">Lucro líquido</span>
          <span className="text-[#d4a04c] font-bold text-lg">R$ 185.696</span>
        </div>
        <div className="flex justify-end">
          <span className="text-[10px] text-emerald-400/80 uppercase tracking-[0.18em]">margem 35,4%</span>
        </div>
      </div>
    </div>
  );
}

// ─── Mock: Situação da operação ────────────────────────────────────
function LiveOpsMock() {
  const tables = [
    { n: "01", s: "ocupada", min: 18 },
    { n: "02", s: "preparando", min: 6 },
    { n: "03", s: "livre" },
    { n: "04", s: "ocupada", min: 42 },
    { n: "05", s: "fechando" },
    { n: "06", s: "ocupada", min: 11 },
  ];
  const cls = (s) =>
    ({
      ocupada: "bg-[#c87a3f]/15 border-[#c87a3f]/40 text-[#d4a04c]",
      preparando: "bg-amber-500/10 border-amber-500/30 text-amber-300",
      livre: "bg-white/[0.03] border-white/[0.08] text-[#8b8b94]",
      fechando: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    }[s]);
  return (
    <div
      className="bg-[#121212] border border-white/[0.08] rounded-2xl p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
      data-testid="hero-liveops-mock"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#c87a3f] font-semibold">Situação da operação</div>
          <div className="text-[#f1f1ef] font-[Fraunces] text-xl tracking-tight mt-1">Salão · 12 mesas</div>
        </div>
        <div className="text-right">
          <div className="text-[#d4a04c] font-mono text-2xl">14:38</div>
          <div className="text-[10px] text-[#8b8b94] uppercase tracking-wider">turno almoço</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {tables.map((t) => (
          <div key={t.n} className={`border rounded-lg p-2 text-center ${cls(t.s)}`}>
            <div className="font-mono text-base font-semibold">{t.n}</div>
            <div className="text-[9px] uppercase tracking-wider opacity-80">{t.s}</div>
            {t.min && <div className="text-[9px] mt-0.5 opacity-70">{t.min}min</div>}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[11px] font-mono pt-3 border-t border-white/[0.06]">
        <span className="text-[#8b8b94]">cozinha · 4 pedidos</span>
        <span className="text-emerald-400">tempo médio 8m12s</span>
      </div>
    </div>
  );
}

// ─── Mock: OPLYX Intelligence insight ─────────────────────────────
function IntelligenceMock() {
  return (
    <div
      className="bg-[#0d0d0d] border border-[#c87a3f]/30 rounded-2xl p-5 shadow-[0_20px_60px_-15px_rgba(200,122,63,0.3)]"
      data-testid="hero-intelligence-mock"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5 bg-[#c87a3f] rounded-full animate-pulse" />
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#c87a3f] font-semibold">OPLYX Intelligence</div>
      </div>
      {/* Sinal externo — mais diferenciador que insight genérico de CMV */}
      <div className="bg-[#c87a3f]/[0.08] border border-[#c87a3f]/20 rounded-xl p-3 mb-3">
        <div className="text-[10px] text-[#c87a3f] font-semibold uppercase tracking-wider mb-1.5">Sinal externo · amanhã</div>
        <div className="text-sm text-[#f1f1ef] leading-snug">
          🌧 Previsão de chuva: bebidas quentes tendem a subir{" "}
          <span className="text-[#c87a3f] font-semibold">+18%</span> com base no histórico
        </div>
      </div>
      <div className="font-mono text-[11px] text-[#8b8b94] flex items-center gap-2">
        <span className="text-emerald-400">→</span>
        Considere aumentar estoque de leite
      </div>
    </div>
  );
}

// ─── Mock: Operação offline ────────────────────────────────────────
function OfflineMock() {
  return (
    <div
      className="bg-[#121212] border border-white/[0.08] rounded-2xl p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
      data-testid="offline-mock"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-400">Operando offline</span>
        </div>
        <span className="font-mono text-[10px] text-[#8b8b94]">22:47</span>
      </div>
      <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-4 mb-4">
        <div className="text-[10px] uppercase tracking-wider text-[#8b8b94] mb-2">Venda em andamento</div>
        <div className="space-y-1.5 font-mono text-[12px]">
          <div className="flex justify-between">
            <span className="text-[#a1a1aa]">Espresso duplo</span>
            <span className="text-[#f1f1ef]">R$ 9,00</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#a1a1aa]">Croissant manteiga</span>
            <span className="text-[#f1f1ef]">R$ 14,00</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-white/[0.06]">
            <span className="text-[#f1f1ef] font-semibold">Total</span>
            <span className="text-emerald-400 font-semibold">R$ 23,00</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div className="bg-amber-400/[0.06] border border-amber-400/20 rounded-lg px-3 py-2.5 text-center">
          <div className="font-mono text-xl text-amber-400 font-semibold">4</div>
          <div className="text-[#8b8b94] mt-0.5">vendas na fila</div>
        </div>
        <div className="bg-amber-400/[0.06] border border-amber-400/20 rounded-lg px-3 py-2.5 text-center">
          <div className="font-mono text-xl text-amber-400 font-semibold">3</div>
          <div className="text-[#8b8b94] mt-0.5">NFC-e pendentes</div>
        </div>
      </div>
      <div className="mt-4 text-[10px] text-[#8b8b94] text-center tracking-wide">
        sincroniza sozinho quando a internet volta · sem perder dados
      </div>
    </div>
  );
}

// ─── Mock: Meta do dia ─────────────────────────────────────────────
// ─── Section: Hero ─────────────────────────────────────────────────
function Hero({ onPrimary }) {
  return (
    <section id="top" className="relative pt-40 pb-56 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[700px] rounded-full bg-[#2A8A58]/[0.06] blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] rounded-full bg-[#b66a32]/[0.05] blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="inline-flex items-center gap-2 border border-[#e5e3d8] bg-white/60 rounded-full px-3 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 bg-[#2A8A58] rounded-full" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#52524a] font-semibold">Para quem opera de verdade</span>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-[Fraunces] text-5xl sm:text-6xl lg:text-7xl tracking-[-0.02em] leading-[0.95] text-[#0a0a0a]">
              Enxergue seu negócio{" "}
              <span className="italic text-[#23784c] font-light">enquanto ele acontece</span>.
            </h1>
          </Reveal>
          <Reveal delay={260}>
            <p className="mt-8 text-lg text-[#52524a] leading-relaxed max-w-xl">
              A maioria dos sistemas para no PDV. O OPLYX começa ali e vai até o custo real,
              o ponto do funcionário, a meta do dia, a reposição automática do estoque:
              tudo no mesmo lugar, sem retrabalho e sem planilha paralela.{" "}
              <span className="text-[#0a0a0a] font-medium">Decisões em tempo real, enquanto o turno acontece</span>.
            </p>
          </Reveal>
          <Reveal delay={400}>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={onPrimary}
                className="bg-[#23784c] hover:bg-[#185c3a] text-white font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-[0_8px_30px_-10px_rgba(42,138,88,0.5)]"
                data-testid="hero-cta-trial"
              >
                Contratar a OPLYX
              </button>
  
              <a
                href="https://wa.me/5548991207631?text=Ol%C3%A1%2C%20quero%20agendar%2015%20min%20para%20conhecer%20o%20OPLYX"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-[#0a0a0a]/15 hover:border-[#0a0a0a]/40 text-[#0a0a0a] px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
                data-testid="hero-cta-schedule"
              >
                Agendar 15 min com a gente
              </a>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-[#6b6b62]">
              {["Onboarding humano incluído", "Sem fidelidade", "Migração do sistema atual inclusa"].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-[#2A8A58]" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4L8.5 12l6.8-6.7a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
                  </svg>
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* 3 mocks em composição layered */}
        <div className="lg:col-span-5 relative" style={{ minHeight: 520 }}>
          <Reveal delay={500}>
            <div className="relative" style={{ minHeight: 420 }}>
              <div className="relative z-10 -rotate-1">
                <DreMock />
              </div>
              <div className="absolute top-36 -left-8 z-20 rotate-2 w-[82%]">
                <LiveOpsMock />
              </div>
              <div className="absolute top-56 right-0 z-30 -rotate-1 w-[54%]">
                <IntelligenceMock />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ─── Section: PDV Block (bloco 3) ─────────────────────────────────
function PdvBlock() {
  return (
    <section id="pdv-block" className="py-24 relative bg-white border-y border-[#e5e3d8]">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <p className="font-[Fraunces] text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#0a0a0a]">
            Seu PDV mostra quanto você vendeu.
          </p>
        </Reveal>
        <Reveal delay={100}>
          <p className="font-[Fraunces] text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#0a0a0a] mt-3">
            Mas ele mostra{" "}
            <span className="italic text-[#23784c] font-light">quanto sobrou?</span>
          </p>
        </Reveal>
        <Reveal delay={220}>
          <p className="mt-10 text-lg text-[#52524a] leading-relaxed max-w-2xl mx-auto">
            Em cafeteria e restaurante, vender é só metade da história.
          </p>
        </Reveal>
        <Reveal delay={300}>
          <p className="mt-5 text-lg text-[#52524a] leading-relaxed max-w-2xl mx-auto">
            O lucro escapa quando o fornecedor aumenta preço, a ficha técnica fica desatualizada, o estoque não baixa certo,
            o caixa fecha com diferença, o iFood cresce sem margem e o DRE só aparece quando o mês já acabou.
          </p>
        </Reveal>
        <Reveal delay={400}>
          <p className="mt-8 text-xl text-[#0a0a0a] font-medium leading-relaxed max-w-2xl mx-auto">
            A OPLYX conecta venda, fiscal, estoque, compras, CMV, equipe, financeiro e DRE para você enxergar a operação enquanto ela acontece.
          </p>
        </Reveal>
        <Reveal delay={500}>
          <p className="mt-8 text-lg text-[#52524a] leading-relaxed">Mais um relatório não resolve isso.</p>
          <p className="mt-2 text-xl text-[#0a0a0a] font-semibold font-[Fraunces] italic">O que resolve é parar de decidir no escuro.</p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Section: Problema (bloco 4) ──────────────────────────────────
function ProblemSection() {
  const items = [
    "A venda acontece no PDV.",
    "O custo mora numa planilha à parte.",
    "A compra fica perdida na nota do fornecedor.",
    "Conferir estoque ainda é contar na mão.",
    "O financeiro fecha depois, sempre depois.",
    "E quando o DRE aparece, já é tarde.",
  ];
  return (
    <section id="problema" className="py-32 relative">
      <div className="max-w-5xl mx-auto px-6">
        <Reveal>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] max-w-3xl leading-[1.05]">
            Seu caixa vende. Mas o resto da operação{" "}
            <span className="italic text-[#9a5a29] font-light">continua no escuro</span>.
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-12 space-y-4 max-w-2xl">
            {items.map((item, i) => (
              <div key={i} className="flex items-start gap-4 text-[#52524a] text-lg leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#b66a32] flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={400}>
          <p className="mt-10 text-xl text-[#0a0a0a] font-medium leading-relaxed max-w-2xl">
            Quando cada parte da operação mora em um lugar, o dono só descobre o problema depois que o dinheiro já escapou.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Section: Manifesto ────────────────────────────────────────────
function Manifesto() {
  return (
    <section id="manifesto" className="py-32 relative">
      <div className="max-w-5xl mx-auto px-6">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold mb-8">Manifesto</div>
        </Reveal>
        <Reveal delay={100}>
          <p className="font-[Fraunces] text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#0a0a0a] max-w-4xl">
            Sistemas genéricos tratam restaurante como{" "}
            <span className="italic text-[#6b6b62] font-light">empresa que só emite nota</span>.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-8 font-[Fraunces] text-2xl sm:text-3xl tracking-tight text-[#0a0a0a] max-w-3xl">
            Foodservice físico é outra coisa.
          </p>
        </Reveal>
        <Reveal delay={300}>
          <p className="mt-6 text-lg text-[#52524a] leading-relaxed max-w-2xl">
            É fila no caixa, comanda aberta, ingrediente acabando, fornecedor mudando preço, funcionário batendo ponto,
            venda no iFood, fechamento no fim do turno e uma pergunta que o dono precisa responder todo dia:
          </p>
        </Reveal>
        <Reveal delay={380}>
          <p className="mt-6 font-[Fraunces] text-2xl sm:text-3xl italic text-[#23784c] font-light max-w-2xl">
            a operação deu lucro hoje?
          </p>
        </Reveal>
        <Reveal delay={460}>
          <p className="mt-8 text-lg text-[#52524a] leading-relaxed max-w-2xl">
            A OPLYX nasceu para responder isso enquanto a loja funciona.
          </p>
        </Reveal>
        <Reveal delay={540}>
          <p className="mt-5 text-lg text-[#52524a] leading-relaxed max-w-2xl">
            Caixa, fiscal, estoque, compras, ficha técnica, equipe, financeiro e DRE nascem na mesma base.
            Não tem relatório de PDV numa aba, planilha de CMV noutra e extrato bancário separado para você juntar depois.
          </p>
        </Reveal>
        <Reveal delay={620}>
          <p className="mt-6 text-xl text-[#0a0a0a] font-medium leading-relaxed max-w-2xl">
            Assim dá para responder sem esperar o fechamento.
          </p>
        </Reveal>
        <Reveal delay={500}>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#e5e3d8] border border-[#e5e3d8] rounded-2xl overflow-hidden max-w-3xl">
            {[
              { v: 524, prefix: "R$ ", suffix: "k", k: "Receita rastreada / mês (cliente médio)" },
              { v: 35, suffix: "%", k: "Margem líquida visível em tempo real" },
              { v: 23, suffix: " min", k: "Tempo médio até o gestor agir sobre uma queda de venda" },
            ].map((m, i) => (
              <div key={i} className="bg-white p-7">
                <div className="font-[Fraunces] text-4xl text-[#9a5a29] font-light tracking-tight">
                  <Counter to={m.v} prefix={m.prefix} suffix={m.suffix} />
                </div>
                <div className="mt-2 text-xs text-[#52524a] leading-relaxed">{m.k}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Section: Pilares (Bento) ──────────────────────────────────────
function Pillar({ children, className = "", testid }) {
  return (
    <div
      className={`group relative bg-white border border-[#e5e3d8] rounded-2xl p-7 overflow-hidden transition-all duration-500 hover:-translate-y-0.5 hover:border-[#0a0a0a]/15 hover:shadow-[0_20px_50px_-25px_rgba(10,10,10,0.15)] ${className}`}
      data-testid={testid}
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#2A8A58]/0 to-transparent group-hover:via-[#2A8A58]/50 transition-colors duration-700" />
      {children}
    </div>
  );
}

function Pillars() {
  return (
    <section id="pilares" className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold mb-4">Como funciona</div>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] max-w-3xl leading-[1.05]">
            Quatro partes da operação{" "}
            <span className="italic text-[#23784c] font-light">que finalmente conversam entre si</span>.
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-6 text-[#52524a] text-lg leading-relaxed max-w-2xl">
            Cada venda alimenta estoque, fiscal, CMV, financeiro e DRE ao mesmo tempo. Você não digita a mesma informação duas vezes em lugares diferentes.
          </p>
        </Reveal>

        {/* 4 blocos de valor */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Reveal delay={100}>
            <Pillar testid="pillar-sell" className="h-full">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#23784c] font-semibold">Venda</div>
              <h3 className="font-[Fraunces] text-2xl text-[#0a0a0a] mt-2 leading-tight">
                Venda e emita <span className="italic text-[#23784c] font-light">sem travar a operação</span>
              </h3>
              <p className="mt-3 text-sm text-[#52524a] leading-relaxed">
                PDV web, comandas, mesas, NFC-e e funcionamento offline para o caixa continuar vendendo mesmo quando a internet cai.
              </p>
            </Pillar>
          </Reveal>
          <Reveal delay={150}>
            <Pillar testid="pillar-cost" className="h-full">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#9a5a29] font-semibold">Custo</div>
              <h3 className="font-[Fraunces] text-2xl text-[#0a0a0a] mt-2 leading-tight">
                Saiba o custo real <span className="italic text-[#23784c] font-light">do que você vende</span>
              </h3>
              <p className="mt-3 text-sm text-[#52524a] leading-relaxed">
                Cada venda baixa ingredientes da ficha técnica. Quando uma compra chega com preço novo, o CMV é recalculado e você enxerga quais produtos estão perdendo margem.
              </p>
            </Pillar>
          </Reveal>
          <Reveal delay={200}>
            <Pillar testid="pillar-financial" className="h-full">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b62] font-semibold">Financeiro</div>
              <h3 className="font-[Fraunces] text-2xl text-[#0a0a0a] mt-2 leading-tight">
                Pare de fechar o negócio <span className="italic text-[#23784c] font-light">em planilha</span>
              </h3>
              <p className="mt-3 text-sm text-[#52524a] leading-relaxed">
                Vendas, compras, formas de pagamento, conciliação, custos e DRE caem no mesmo lugar. Você acompanha o resultado do dia sem esperar o fim do mês.
              </p>
            </Pillar>
          </Reveal>
          <Reveal delay={250}>
            <Pillar testid="pillar-decision" className="h-full">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#9a5a29] font-semibold">Decisão</div>
              <h3 className="font-[Fraunces] text-2xl text-[#0a0a0a] mt-2 leading-tight">
                Decida <span className="italic text-[#23784c] font-light">antes do prejuízo aparecer</span>
              </h3>
              <p className="mt-3 text-sm text-[#52524a] leading-relaxed">
                Metas do dia, reposição de estoque, alertas de custo, desempenho por produto e sinais externos ajudam você a decidir antes do prejuízo aparecer.
              </p>
            </Pillar>
          </Reveal>
        </div>

        {/* 3 sistemas que saem da conta */}
        <div id="sistemas" className="mt-24">
          <Reveal>
            <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] max-w-3xl leading-[1.05]">
              Três sistemas que{" "}
              <span className="italic text-[#9a5a29] font-light">saem da sua conta no fim do mês</span>.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Reveal delay={100}>
              <Pillar testid="pillar-repp" className="h-full">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b62] font-semibold">Equipe</div>
                <h3 className="font-[Fraunces] text-2xl text-[#0a0a0a] mt-2 leading-tight">
                  Ponto eletrônico <span className="italic text-[#23784c] font-light">da equipe</span>
                </h3>
                <p className="mt-3 text-sm text-[#52524a] leading-relaxed">
                  Banco de horas, escala, turnos e gorjetas dentro do mesmo sistema em que a operação acontece. Você deixa de contratar e conciliar um software de ponto separado.
                </p>
              </Pillar>
            </Reveal>
            <Reveal delay={150}>
              <Pillar testid="pillar-ifood" className="h-full">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#9a5a29] font-semibold">Delivery</div>
                <h3 className="font-[Fraunces] text-2xl text-[#0a0a0a] mt-2 leading-tight">
                  iFood <span className="italic text-[#23784c] font-light">sem relançamento manual</span>
                </h3>
                <p className="mt-3 text-sm text-[#52524a] leading-relaxed">
                  O pedido entra sozinho, vai para a cozinha e aparece no financeiro do delivery. Ninguém da sua equipe digita pedido duas vezes.
                </p>
              </Pillar>
            </Reveal>
            <Reveal delay={200}>
              <Pillar testid="pillar-kds" className="h-full">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b62] font-semibold">Cozinha</div>
                <h3 className="font-[Fraunces] text-2xl text-[#0a0a0a] mt-2 leading-tight">
                  Monitor de cozinha <span className="italic text-[#23784c] font-light">(KDS)</span>
                </h3>
                <p className="mt-3 text-sm text-[#52524a] leading-relaxed">
                  O que foi vendido aparece na produção na hora, sem comanda de papel e sem grito de balcão.
                </p>
              </Pillar>
            </Reveal>
          </div>
          <Reveal delay={300}>
            <div className="mt-6 border border-[#e5e3d8] bg-white rounded-xl px-6 py-4 text-sm text-[#52524a] leading-relaxed">
              Também incluído: comandas no celular do garçom · gorjetas por turno · checklists operacionais · multi-filial · CRM com histórico e cashback · assistente em português.
            </div>
          </Reveal>
        </div>

      </div>
    </section>
  );
}

// ─── Section: Migração — Agente de Onboarding ─────────────────────
function OnboardingAgentSection() {
  const steps = [
    {
      t: "Você envia o que já tem",
      d: "Planilha de produtos e clientes, XML de compra do fornecedor, até foto da ficha técnica escrita à mão: o agente aceita o formato que você já usa.",
    },
    {
      t: "O agente organiza e casa com o seu cadastro",
      d: "Identifica produtos, ingredientes, clientes e histórico de compra. Compara com o que já existe na sua conta antes de sugerir algo novo. Nunca inventa item que não veio do seu arquivo.",
    },
    {
      t: "Você revisa e confirma",
      d: "Correspondência óbvia vem pronta para aprovar. Caso incerto vai para revisão manual, separado do resto. Nada entra no cadastro real sem sua confirmação.",
    },
  ];
  const rows = [
    { old: '"Café Especial 500g" (planilha antiga)', matched: "Café arábica torrado · ingrediente", conf: "94%", confClass: "text-[#4BB87A]", chip: "Confirmar", chipClass: "bg-[#4BB87A]/10 text-[#4BB87A] border-[#4BB87A]/30" },
    { old: '"Leite Integral 1L Piracanjuba"', matched: "Leite integral · ingrediente", conf: "89%", confClass: "text-[#4BB87A]", chip: "Confirmar", chipClass: "bg-[#4BB87A]/10 text-[#4BB87A] border-[#4BB87A]/30" },
    { old: '"Xarope Baunilha 750ml"', matched: "sem correspondência no cadastro", conf: "—", confClass: "text-[#8b8b94]", chip: "Revisar", chipClass: "bg-amber-400/10 text-amber-400 border-amber-400/30" },
  ];
  return (
    <section id="migracao" className="py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#4BB87A] font-semibold">Migração · Agente de Onboarding</div>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-white mt-5 leading-[1.08] max-w-2xl">
            Seu cadastro antigo não precisa ser{" "}
            <span className="italic text-[#4BB87A] font-light">digitado de novo</span>.
          </h2>
        </Reveal>
        <Reveal delay={180}>
          <p className="mt-6 text-[#a3a398] text-lg leading-relaxed max-w-xl">
            Envie os arquivos do seu sistema atual. Um agente lê, organiza e mostra exatamente o que vai entrar no
            seu cadastro OPLYX. Você só confirma.
          </p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            {steps.map((s, i) => (
              <Reveal key={i} delay={220 + i * 90}>
                <div className={`flex gap-5 py-6 ${i < steps.length - 1 ? "border-b border-white/10" : ""}`}>
                  <div className="font-[Fraunces] text-sm text-[#d4a04c] w-6 flex-shrink-0 pt-0.5">0{i + 1}</div>
                  <div>
                    <div className="text-base font-medium text-[#f1f1ef]">{s.t}</div>
                    <p className="mt-1.5 text-sm text-[#a3a398] leading-relaxed">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={300}>
              <div className="bg-[#121212] border border-white/[0.08] rounded-2xl p-7" data-testid="onboarding-agent-mock">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#c89248] font-semibold">Migração · sugestões pendentes</div>
                    <div className="font-[Fraunces] text-xl text-[#f1f1ef] mt-1">Importado do seu sistema anterior</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[#a78bfa] border border-[#a78bfa]/30 bg-[#a78bfa]/[0.08] rounded-full px-2.5 py-1">3 itens</span>
                </div>
                {rows.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-3.5 border-t border-white/[0.05] first:border-t-0">
                    <div className="min-w-0">
                      <div className="text-[11px] text-[#8b8b94] font-mono truncate">{r.old}</div>
                      <div className="text-sm text-[#f1f1ef] mt-0.5">→ {r.matched}</div>
                    </div>
                    <span className={`font-mono text-xs font-semibold flex-shrink-0 ${r.confClass}`}>{r.conf}</span>
                    <span className={`text-[10px] uppercase tracking-wider border rounded-full px-2.5 py-1 flex-shrink-0 ${r.chipClass}`}>{r.chip}</span>
                  </div>
                ))}
                <div className="mt-5 pt-4 border-t border-white/[0.06] text-[11px] text-[#8b8b94] leading-relaxed">
                  <span className="text-[#a3a398]">Fila de revisão manual:</span> casos abaixo de 50% de confiança nunca entram sozinhos. Ficam separados até você decidir.
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Comparativo (bloco 9) ───────────────────────────────
function ComparisonSection() {
  const basics = [
    "PDV com NFC-e",
    "comandas e mesas",
    "estoque",
    "relatórios",
    "financeiro básico",
    "cadastro de clientes",
  ];
  const differentials = [
    { label: "CMV recalculado quando o custo da compra muda", desc: "Cada compra atualiza o custo dos ingredientes e recalcula a margem de cada produto" },
    { label: "DRE em tempo real por período e unidade", desc: "Operação e financeiro atualizados a cada venda, durante o turno" },
    { label: "Central Financeira com saldo por conta, dia a dia", desc: "Caixa, banco, maquininha e repasse de delivery, cada um isolado. Nunca misturados como se fossem o mesmo dinheiro" },
    { label: "Caixa offline no navegador, sem instalação", desc: "Se a internet cair, você continua vendendo. Funciona direto no navegador" },
    { label: "Fiscal integrado ao fluxo de venda", desc: "NFC-e no mesmo clique da venda, sem janela paralela ou módulo separado" },
    { label: "iFood conectado ao KDS e ao financeiro", desc: "Pedido recebido, confirmado e no monitor da cozinha automaticamente" },
    { label: "Metas calculadas com base nos custos reais", desc: "A meta do dia considera CMV, custos fixos e histórico, não estimativa" },
    { label: "Ponto, gorjetas e operação por turno", desc: "Banco de horas, escala e gorjetas integrados à operação" },
    { label: "Migração assistida por agente, não digitação manual", desc: "Envie os arquivos do sistema antigo: o agente organiza e você só confirma" },
    { label: "Multi-filial no mesmo sistema, com filial adicional contratada quando precisar", desc: "Estoque, caixa, equipe e DRE isolados por unidade" },
  ];
  return (
    <section id="diferenciais" className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold mb-4">Comparativo</div>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] max-w-3xl leading-[1.05]">
            PDV é o começo.{" "}
            <span className="italic text-[#9a5a29] font-light">O problema é o que acontece depois da venda.</span>
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-6 text-[#52524a] text-lg leading-relaxed max-w-2xl">
            A maioria dos sistemas registra a venda. A OPLYX conecta a venda ao custo, ao estoque, ao fiscal, à equipe e ao resultado do dia.
          </p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          {/* Esquerda: o básico */}
          <Reveal delay={250}>
            <div className="bg-white border border-[#e5e3d8] rounded-2xl p-8 h-full">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#6b6b62] font-semibold mb-2">
                Todo sistema precisa ter
              </div>
              <p className="text-sm text-[#6b6b62] mb-6">Isso é o básico.</p>
              <ul className="space-y-3">
                {basics.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#6b6b62]">
                    <span className="w-5 h-5 rounded-full border border-[#d4d4d0] flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4L8.5 12l6.8-6.7a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* Direita: o que a OPLYX entrega junto */}
          <div className="bg-[#0a0a0a] border border-[#0a0a0a] rounded-2xl p-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#2A8A58] font-semibold mb-2">
              O que a OPLYX entrega junto, na mesma base
            </div>
            <ul className="space-y-5 mt-6">
              {differentials.map((item, i) => (
                <Reveal key={i} delay={300 + i * 70}>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#2A8A58]/15 border border-[#2A8A58]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-emerald-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4L8.5 12l6.8-6.7a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <div>
                      <div className="text-[#f1f1ef] font-medium leading-snug">{item.label}</div>
                      <div className="text-[#8b8b94] text-sm mt-0.5 leading-snug">{item.desc}</div>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Justificativa do preço (bloco 10) ───────────────────
function PriceJustification() {
  const rows = [
    { left: "Software de ponto eletrônico, cobrado por funcionário", right: "incluído" },
    { left: "Emissão fiscal contratada à parte", right: "incluída (franquia no Basic, ilimitada no Full)" },
    { left: "Planilha de CMV mantida na mão", right: "recusteio automático a cada compra" },
    { left: "Relançamento manual de pedidos de delivery", right: "pedido entra sozinho" },
    { left: "Fechamento de mês em planilha", right: "DRE fechando durante o turno" },
  ];
  return (
    <section id="preco-justificativa" className="py-32 relative bg-white border-y border-[#e5e3d8]">
      <div className="max-w-5xl mx-auto px-6">
        <Reveal>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] max-w-3xl leading-[1.05]">
            Por que a OPLYX não custa{" "}
            <span className="italic text-[#9a5a29] font-light">o preço de um PDV simples?</span>
          </h2>
        </Reveal>
        <Reveal delay={150}>
          <p className="mt-8 text-lg text-[#52524a] leading-relaxed max-w-2xl">
            Porque o problema que ela resolve não termina quando a venda passa no caixa.
          </p>
        </Reveal>
        <Reveal delay={250}>
          <p className="mt-4 text-[#52524a] leading-relaxed max-w-2xl">
            Um PDV barato registra a venda e emite nota. O resto da operação continua espalhado, e cada pedaço tem um custo próprio:
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10 border border-[#e5e3d8] rounded-2xl overflow-hidden">
            <div className="grid grid-cols-2 bg-[#f5f4ee] border-b border-[#e5e3d8] px-6 py-3">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6b6b62]">O que a operação paga hoje</div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#23784c]">Com a OPLYX</div>
            </div>
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-2 border-b border-[#e5e3d8] last:border-0 px-6 py-4 gap-4">
                <div className="text-sm text-[#52524a] leading-snug">{row.left}</div>
                <div className="text-sm text-[#23784c] font-medium leading-snug">{row.right}</div>
              </div>
            ))}
            <div className="grid grid-cols-2 bg-[#f5f4ee] px-6 py-4 gap-4 border-t border-[#e5e3d8]">
              <div className="text-sm text-[#52524a] leading-snug">Numa operação como a nossa, isso representa</div>
              <div className="text-sm text-[#9a5a29] font-semibold">Sistemas, planilhas e horas de fechamento, todo mês</div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={450}>
          <p className="mt-10 text-lg text-[#52524a] leading-relaxed max-w-2xl">
            A conta não é o preço da mensalidade de um PDV contra o de outro. A conta é: quanto custa operar no escuro?
          </p>
        </Reveal>
        <Reveal delay={520}>
          <p className="mt-5 text-xl text-[#0a0a0a] font-medium leading-relaxed max-w-2xl">
            Se a OPLYX evitar um erro de compra, uma perda de estoque, um preço desatualizado ou uma decisão errada de margem, ela começa a se pagar.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Section: Caixa offline ────────────────────────────────────────
function OfflineSection() {
  return (
    <section id="offline" className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold mb-5">Resiliência operacional</div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] leading-[1.1]">
                Sábado à noite.<br />
                Casa cheia.<br />
                <span className="italic text-[#9a5a29] font-light">Internet caiu.</span>
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-8 text-lg text-[#52524a] leading-relaxed max-w-xl">
                O caixa não pode parar porque o Wi-Fi resolveu cair.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <p className="mt-5 text-[#52524a] leading-relaxed max-w-xl">
                Com a OPLYX, sua equipe continua vendendo, abrindo comandas e registrando o turno no navegador.
                As NFC-e ficam em contingência e a sincronização acontece automaticamente quando a conexão volta.
              </p>
            </Reveal>
            <Reveal delay={380}>
              <div className="mt-8 space-y-2 max-w-md">
                {[
                  "Sem instalar sistema em máquina Windows.",
                  "Sem parar a fila.",
                  "Sem transformar queda de internet em caos no caixa.",
                ].map((feat) => (
                  <div key={feat} className="flex items-start gap-2 text-sm text-[#52524a]">
                    <span className="text-[#23784c] flex-shrink-0 mt-0.5">✓</span>
                    {feat}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={300}><OfflineMock /></Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: OPLYX Intelligence ──────────────────────────────────
function IntelligenceSection() {
  const questions = [
    "quanto preciso vender hoje para bater a meta?",
    "quais produtos vendem bem, mas deixam pouca margem?",
    "o que devo comprar antes de acabar?",
    "como chuva, feriado e dia de pagamento mudam minha demanda?",
  ];
  return (
    <section id="intelligence" className="py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#b66a32] font-semibold">Add-on · OPLYX Intelligence</div>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-white mt-5 leading-[1.05] max-w-3xl">
            O CMV da sua operação pode estar{" "}
            <span className="italic text-[#b66a32] font-light">sangrando em silêncio</span>.
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-6 text-[#a3a398] text-lg leading-relaxed max-w-2xl">
            Muito bar e restaurante vende bem e mesmo assim fecha o mês no vermelho. Quase sempre o motivo está no custo que ninguém acompanhou a tempo.
          </p>
        </Reveal>

        {/* Cálculo concreto */}
        <div className="mt-16 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="bg-[#141414] border border-[#2a2a2a] rounded-2xl p-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#52524a] font-semibold">O que 1 ponto percentual de CMV custa</div>
              <div className="mt-6 space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-[#2a2a2a]">
                  <span className="text-[#a3a398] text-sm">Operação de R$&nbsp;80&nbsp;k/mês de receita</span>
                  <span className="text-white font-semibold tabular-nums">R$&nbsp;80.000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#2a2a2a]">
                  <span className="text-[#a3a398] text-sm">CMV real médio do setor</span>
                  <span className="text-[#e07b3b] font-semibold tabular-nums">28 – 33%</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#2a2a2a]">
                  <span className="text-[#a3a398] text-sm">Meta saudável de CMV</span>
                  <span className="text-[#2A8A58] font-semibold tabular-nums">25%</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#2a2a2a]">
                  <span className="text-[#a3a398] text-sm">Cada 1 pp acima da meta custa</span>
                  <span className="text-[#e07b3b] font-semibold tabular-nums">R$&nbsp;800/mês</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-[#1a1a1a] rounded-xl px-4 -mx-4">
                  <span className="text-white text-sm font-medium">5 pp fora do controle ao longo de um ano</span>
                  <span className="text-[#e07b3b] text-lg font-bold tabular-nums">− R$&nbsp;48.000</span>
                </div>
              </div>
              <p className="mt-6 text-[#52524a] text-xs leading-relaxed">
                Referência: CMV médio do foodservice brasileiro 28–33% (Abrasel, 2024). Meta recomendada: 25%.
              </p>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="bg-[#141414] border border-[#2a2a2a] rounded-2xl p-8 flex flex-col gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#a1a1aa] font-semibold">O insumo subiu. A ficha técnica, não.</div>
                <p className="mt-4 text-[#a3a398] text-sm leading-relaxed">
                  O café moído subiu 39,6% em 2024 (IPCA/IBGE). Sem atualização automática de ficha técnica, o dono só descobre a perda no fechamento, quando já é tarde.
                </p>
                <p className="mt-3 text-[#a3a398] text-sm leading-relaxed">
                  A OPLYX recalcula o CMV de cada produto toda vez que uma compra é registrada. Você vê a margem real, não a margem de quando o cardápio foi montado.
                </p>
              </div>
              <div className="border-t border-[#2a2a2a] pt-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#a1a1aa] font-semibold">Clima e sazonalidade na demanda</div>
                <p className="mt-4 text-[#a3a398] text-sm leading-relaxed">
                  Clima, feriado e dia da semana mudam muito o movimento de uma cafeteria. Uma tarde de chuva num dia de semana tem demanda completamente diferente de um sábado ensolarado.
                </p>
                <p className="mt-3 text-[#a3a398] text-sm leading-relaxed">
                  O Intelligence cruza histórico de vendas com padrões de dia, feriado e clima para estimar a demanda antes de você abrir o caixa.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Perguntas respondidas */}
        <Reveal delay={100}>
          <div className="mt-12">
            <p className="text-[#52524a] text-sm uppercase tracking-[0.2em] font-semibold mb-5">O que o Intelligence responde</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {questions.map((q, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#141414] border border-[#2a2a2a] rounded-xl px-5 py-4 text-sm text-[#a3a398] leading-relaxed">
                  <span className="text-[#b66a32] flex-shrink-0 mt-0.5 font-bold">—</span>
                  {q}
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <p className="mt-10 text-[#52524a] text-sm leading-relaxed max-w-xl">
            Nenhum sistema de PDV nacional oferece esse tipo de análise integrada ao caixa, estoque e compras. Ferramentas internacionais similares (MarketMan, Restaurant365) operam apenas em USD e sem NFC-e.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Section: Central Financeira ──────────────────────────────────
function CentralFinanceiraSection() {
  const ledger = [
    { day: "24/08", in: "+4.820", out: "−1.960", bal: "12.340" },
    { day: "25/08", in: "+5.110", out: "−2.240", bal: "15.210" },
    { day: "26/08", in: "+3.980", out: "−4.500", bal: "14.690" },
    { day: "27/08", in: "+6.240", out: "−1.180", bal: "19.750" },
    { day: "28/08", in: "+5.560", out: "−2.010", bal: "23.300" },
  ];
  const accounts = [
    { nm: "Caixa físico", sub: "dinheiro em espécie", amt: "R$ 1.240" },
    { nm: "Conta PJ · Itaú", sub: "PIX e transferências", amt: "R$ 18.560" },
    { nm: "Maquininha Stone", sub: "a receber, D+1", amt: "R$ 3.120" },
    { nm: "Repasse iFood", sub: "a receber do próprio iFood", amt: "R$ 890" },
  ];
  return (
    <section id="central-financeira" className="py-32 relative bg-white border-y border-[#e5e3d8]">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold mb-4">Dinheiro · Central Financeira</div>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] leading-[1.08] max-w-2xl">
            O dinheiro sai da venda{" "}
            <span className="italic text-[#23784c] font-light">e você sabe onde ele está</span>.
          </h2>
        </Reveal>
        <Reveal delay={180}>
          <p className="mt-6 text-[#52524a] text-lg leading-relaxed max-w-xl">
            Caixa físico, conta bancária, maquininha e repasse de delivery: cada real cai na conta certa, na data
            certa, com saldo acumulado dia a dia.
          </p>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-4 text-[#52524a] leading-relaxed max-w-xl">
            Sangria, conta a pagar e conciliação de extrato batem sozinhos no fluxo de caixa. Você para de montar
            planilha para saber quanto tem de verdade.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-6">
            <Reveal delay={280}>
              <div className="bg-[#121212] border border-white/[0.08] rounded-2xl p-7" data-testid="cashflow-mock">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#c87a3f] font-semibold">Fluxo de caixa</div>
                    <div className="font-[Fraunces] text-xl text-[#f1f1ef] mt-1">Saldo acumulado</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-emerald-400 border border-emerald-400/30 bg-emerald-400/[0.05] rounded-full px-2.5 py-1">ao vivo</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-[10px] font-mono text-[#8b8b94] uppercase tracking-wider pb-2 border-b border-white/[0.08]">
                  <span>Dia</span><span className="text-right">Entradas</span><span className="text-right">Saídas</span><span className="text-right">Saldo</span>
                </div>
                {ledger.map((r, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 text-[12px] font-mono py-2.5 border-b border-white/[0.04] last:border-0">
                    <span className="text-[#a1a1aa]">{r.day}</span>
                    <span className="text-right text-emerald-400">{r.in}</span>
                    <span className="text-right text-[#e07b3b]">{r.out}</span>
                    <span className="text-right text-[#d4a04c] font-semibold">{r.bal}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={340}>
              <div className="bg-[#121212] border border-white/[0.08] rounded-2xl p-7" data-testid="accounts-mock">
                <div className="mb-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#c87a3f] font-semibold">Contas</div>
                  <div className="font-[Fraunces] text-xl text-[#f1f1ef] mt-1">Onde o dinheiro está agora</div>
                </div>
                {accounts.map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-3.5 border-t border-white/[0.05] first:border-t-0">
                    <div>
                      <div className="text-sm text-[#f1f1ef]">{a.nm}</div>
                      <div className="text-[11px] text-[#8b8b94] mt-0.5">{a.sub}</div>
                    </div>
                    <div className="font-mono text-sm text-[#f1f1ef] font-semibold">{a.amt}</div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={400}>
              <div className="mt-5 rounded-2xl px-6 py-5 bg-[#b66a32]/[0.06] border border-[#b66a32]/20">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#b66a32] font-bold mb-2">Correção recente</div>
                <p className="text-sm text-[#52524a] leading-relaxed">
                  <span className="line-through text-[#6b6b62]">Antes: repasse do iFood contava como dinheiro recebido no caixa do dia</span>, inflava o saldo.{" "}
                  <span className="text-[#0a0a0a] font-medium">Agora:</span> cada forma de recebimento cai na conta certa, na data em que o dinheiro realmente chega.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Recusteio automático (bloco 8) ──────────────────────
function ConsumptionShowcase() {
  return (
    <section id="recusteio" className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <Reveal>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold">Custo real · Recusteio automático</div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] mt-5 leading-[1.1]">
                O CMV não muda no fechamento do mês.{" "}
                <span className="italic text-[#23784c] font-light">Muda no momento em que o custo muda.</span>
              </h2>
            </Reveal>
            <Reveal delay={180}>
              <p className="mt-6 text-lg text-[#52524a] leading-relaxed">
                O fornecedor reajusta o preço. A compra entra mais cara. Se a ficha técnica continuar considerando o
                custo antigo, o restaurante segue vendendo normalmente. E a margem começa a desaparecer sem fazer
                barulho.
              </p>
            </Reveal>
            <Reveal delay={260}>
              <p className="mt-5 text-[#52524a] leading-relaxed">
                Quando uma NF-e de fornecedor chega com preço novo, a OPLYX recalcula o custo de cada produto vendido
                na hora. Não semanas depois, no fechamento.
              </p>
            </Reveal>
            <Reveal delay={340}>
              <p className="mt-6 text-xl text-[#0a0a0a] font-medium leading-relaxed border-l-2 border-[#b66a32] pl-4">
                O relatório de fim de mês explica o que aconteceu. Ele não devolve a margem que já foi perdida.
              </p>
            </Reveal>
            <Reveal delay={420}>
              <div className="mt-8 border border-[#e5e3d8] bg-white rounded-2xl p-6" data-testid="cmv-math-card">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b62] font-semibold">O que a demora custa</div>
                <div className="mt-4">
                  <div className="flex justify-between items-baseline gap-4 py-3 border-b border-[#e5e3d8]">
                    <span className="text-sm text-[#52524a] leading-snug">Operação com R$ 80 mil/mês de receita</span>
                    <span className="font-[Fraunces] text-xl text-[#0a0a0a] tabular-nums flex-shrink-0">R$ 80.000</span>
                  </div>
                  <div className="flex justify-between items-baseline gap-4 py-3 border-b border-[#e5e3d8]">
                    <span className="text-sm text-[#52524a] leading-snug">Cada 1 ponto percentual de CMV fora da meta</span>
                    <span className="font-[Fraunces] text-xl text-[#0a0a0a] tabular-nums flex-shrink-0">R$ 800/mês</span>
                  </div>
                  <div className="flex justify-between items-baseline gap-4 py-3 -mx-6 px-6 mt-1 bg-[#b66a32]/[0.06] rounded-lg">
                    <span className="text-sm text-[#0a0a0a] font-medium leading-snug">5 pontos percentuais de diferença, em um único mês</span>
                    <span className="font-[Fraunces] text-2xl font-bold text-[#9a5a29] tabular-nums flex-shrink-0">− R$ 4.000</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-5">
            {/* Mock 1 — consumo e reposição */}
            <Reveal delay={200}>
              <div className="bg-[#0d0d0d] border border-[#0a0a0a]/10 rounded-2xl p-7 shadow-[0_30px_80px_-20px_rgba(10,10,10,0.25)]">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#c89248] font-semibold">Consumo em tempo real</div>
                    <div className="font-[Fraunces] text-xl text-[#f1f1ef] mt-1">Café especial · Estoque crítico</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-amber-400 border border-amber-400/30 bg-amber-400/[0.05] rounded-full px-2.5 py-1">3 dias restantes</span>
                </div>
                <div className="relative pl-6 border-l border-white/[0.08] space-y-4">
                  {[
                    { label: "Vendidos hoje", value: "47 espressos", note: "−846g café" },
                    { label: "Estoque atual", value: "1,1 kg", note: "↓ abaixo do mínimo (2 kg)" },
                    { label: "CMD (7 dias)", value: "310g / dia", note: "baseado em dias com movimento" },
                    { label: "Sugestão de compra", value: "2,5 kg", note: "cobre 7 dias + lead time", highlight: true },
                  ].map((row, i) => (
                    <div key={i} className="relative">
                      <div className={`absolute -left-[26px] top-1.5 w-3 h-3 rounded-full border-2 ${row.highlight ? "bg-[#2A8A58] border-[#2A8A58] animate-pulse" : "bg-[#0d0d0d] border-white/30"}`} />
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-0.5">
                        <span className="font-mono text-xs text-[#8b8b94] w-32">{row.label}</span>
                        <span className={`text-sm font-medium ${row.highlight ? "text-[#4BB87A]" : "text-[#f1f1ef]"}`}>{row.value}</span>
                        <span className="text-[#8b8b94] text-xs font-mono">{row.note}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-7 pt-6 border-t border-white/[0.06] flex items-center justify-between">
                  <div className="text-xs text-[#8b8b94]">Workflow: sugerido → aprovado → pedido → recebido</div>
                  <div className="text-[10px] text-[#4BB87A] border border-[#4BB87A]/30 rounded-full px-2.5 py-1">Conta a pagar gerada auto</div>
                </div>
              </div>
            </Reveal>

            {/* Mock 2 — alerta de reprecificação */}
            <Reveal delay={350}>
              <div className="bg-[#0d0d0d] border border-amber-900/40 rounded-2xl p-7 shadow-[0_30px_80px_-20px_rgba(10,10,10,0.25)]">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#c89248] font-semibold">Recálculo automático de CMV</div>
                    <div className="font-[Fraunces] text-xl text-[#f1f1ef] mt-1">Compra recebida, preço novo</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-amber-400 border border-amber-400/30 bg-amber-400/[0.05] rounded-full px-2.5 py-1">⚠ Ação sugerida</span>
                </div>
                <div className="font-mono text-[12px] space-y-2">
                  <div className="text-[#8b8b94]">
                    Compra recebida: <span className="text-[#f1f1ef]">Café especial</span> — <span className="text-amber-400">R$ 48,00/kg (+12%)</span>
                  </div>
                  <div className="border-t border-white/[0.06] pt-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[#8b8b94]">→</span>
                      <span className="text-[#f1f1ef]">CMV do Espresso:</span>
                      <span className="text-[#8b8b94] line-through">28%</span>
                      <span className="text-amber-400 font-semibold">34%</span>
                      <span className="text-[#8b8b94]">(meta: 25%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#8b8b94]">→</span>
                      <span className="text-[#f1f1ef]">Cappuccino:</span>
                      <span className="text-[#8b8b94] line-through">22%</span>
                      <span className="text-amber-400 font-semibold">26%</span>
                      <span className="text-[#8b8b94]">(meta: 25%)</span>
                    </div>
                  </div>
                  <div className="border-t border-white/[0.06] pt-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[#8b8b94]">→</span>
                      <span className="text-[#a1a1aa]">Espresso, preço sugerido:</span>
                      <span className="text-[#4BB87A] font-semibold">R$ 13,50</span>
                      <span className="text-[#8b8b94]">(atual: R$ 11,00)</span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-5 border-t border-white/[0.06]">
                  <div className="text-[10px] text-[#8b8b94]">Fichas técnicas atualizadas automaticamente · Dashboard atualizado sem recompute manual</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={200}>
          <div className="mt-20 rounded-3xl bg-[#0a0a0a] px-8 py-14 sm:px-14 text-center" data-testid="cmv-quote-banner">
            <p className="font-[Fraunces] text-2xl sm:text-3xl italic font-light text-[#f1f1ef] leading-snug max-w-2xl mx-auto">
              Saber depois é informação.{" "}
              <span className="not-italic font-semibold text-[#4BB87A]">Saber enquanto acontece é gestão.</span>
            </p>
            <div className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[#8b8b94]">a lógica por trás da OPLYX</div>
          </div>
        </Reveal>
        <Reveal delay={260}>
          <p className="mt-8 text-center text-sm text-[#52524a]">
            Recusteio automático já vem no plano Basic, sem custo extra.{" "}
            <a href="#precos" className="text-[#2A8A58] hover:text-[#185c3a] underline underline-offset-4 font-medium">Ver planos →</a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Section: Origem ───────────────────────────────────────────────
function OriginStory() {
  return (
    <section id="origem" className="py-32 relative bg-white border-y border-[#e5e3d8]">
      <div className="max-w-5xl mx-auto px-6">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold mb-6">Origem</div>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl lg:text-6xl tracking-[-0.01em] text-[#0a0a0a] leading-[1.05] max-w-4xl">
            O OPLYX nasceu{" "}
            <span className="italic text-[#23784c] font-light">do outro lado do balcão</span>.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-7 space-y-6 text-lg text-[#52524a] leading-relaxed">
            <Reveal delay={200}><p>Opero foodservice físico desde 2016. Cafeteria, balcão, turno, fechamento de caixa às 23h, conversa difícil com fornecedor que mandou nota com preço errado, álcool em gel que sumiu sem ninguém saber para onde foi.</p></Reveal>
            <Reveal delay={300}><p>Em dez anos, passei por quatro sistemas. Cada troca era a esperança de que o próximo resolvesse. Os "ERPs completos" travavam o caixa na hora de emitir nota. Os "simples" ignoravam custo de verdade. Os modernos eram bonitos por fora, vazios por dentro. Cansei.</p></Reveal>
            <Reveal delay={400}><p className="text-[#0a0a0a] font-medium">O OPLYX é o que sobrou depois de cansar. Construído para mostrar o que o sistema antigo escondia: o custo real, a margem real, o ralo real. Por quem opera todo dia, para quem opera todo dia.</p></Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={400}>
              <div className="bg-[#0a0a0a] text-[#f1f1ef] rounded-2xl p-8 relative overflow-hidden">
                <div className="absolute -top-3 -left-3 bg-[#23784c] text-white text-[10px] uppercase tracking-[0.22em] font-semibold rounded-full px-3 py-1">Por que o OPLYX existe</div>
                <div className="font-[Fraunces] text-2xl sm:text-3xl text-[#f1f1ef] leading-[1.15] tracking-tight mt-2">
                  Depois de operar em quatro sistemas diferentes, todos me mostravam faturamento.{" "}
                  <span className="italic text-[#4BB87A]">Nenhum mostrava a verdade.</span>
                </div>
                <div className="mt-8 pt-6 border-t border-white/10 text-[11px] text-[#a1a1aa] uppercase tracking-[0.18em]">Quem construiu o OPLYX</div>
                <div className="mt-1 text-sm text-[#f1f1ef]">Dono e operador de foodservice desde 2016</div>
              </div>
            </Reveal>
            <Reveal delay={550}>
              <div className="mt-5 grid grid-cols-3 gap-2">
                {[{ v: "10 anos", k: "operando foodservice físico" }, { v: "4 sistemas", k: "testados antes" }, { v: "100%", k: "criado por quem usa na própria operação" }].map((s, i) => (
                  <div key={i} className="bg-[#f5f4ee] border border-[#e5e3d8] rounded-lg p-3 text-center">
                    <div className="font-[Fraunces] text-base text-[#0a0a0a]">{s.v}</div>
                    <div className="text-[10px] text-[#6b6b62] mt-1 leading-tight">{s.k}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Demo técnica ─────────────────────────────────────────
function PurchaseInvoiceMock() {
  return (
    <div className="bg-[#121212] border border-white/[0.08] rounded-2xl p-6" data-testid="demo-purchase-mock">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#c87a3f] font-semibold">Compra · NF-e XML aplicada</div>
          <div className="font-[Fraunces] text-xl text-[#f1f1ef] mt-1">Distribuidora Café Norte</div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-emerald-400 border border-emerald-400/30 bg-emerald-400/[0.05] rounded-full px-2 py-0.5">aplicada</span>
      </div>
      {[
        { n: "Café arábica torrado 500g", m: "Café arábica · ingrediente", s: 92 },
        { n: "Filtro papel coador 103", m: "Filtro 103 · descartável", s: 88 },
        { n: "Açúcar cristal 5kg", m: "Açúcar refinado · ingrediente", s: 76 },
      ].map((it, i) => (
        <div key={i} className="border-t border-white/[0.04] py-3 first:border-t-0">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#f1f1ef]">{it.n}</span>
            <span className="font-mono text-[11px] text-emerald-400">→ {it.s}%</span>
          </div>
          <div className="text-[11px] text-[#8b8b94] mt-0.5">mapeado: {it.m}</div>
        </div>
      ))}
      <div className="mt-5 pt-4 border-t border-white/[0.06] flex justify-between">
        <span className="text-[#8b8b94] text-sm">CMV recalculado</span>
        <span className="font-mono text-[#d4a04c]">27,2%</span>
      </div>
    </div>
  );
}

function CashCloseMock() {
  return (
    <div className="bg-[#121212] border border-white/[0.08] rounded-2xl p-6" data-testid="demo-cash-mock">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#c87a3f] font-semibold">Conferência de caixa</div>
          <div className="font-[Fraunces] text-xl text-[#f1f1ef] mt-1">Turno 14 · 22:18</div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[#d4a04c] font-semibold">PDF gerado</span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-[11px] font-mono mb-3 text-[#8b8b94]">
        <span>Forma</span><span className="text-right">Esperado</span><span className="text-right">Contado</span><span className="text-right">Diferença</span>
      </div>
      {[
        ["Dinheiro", "412,00", "410,00", "−2,00", "amber"],
        ["Crédito", "1.840,50", "1.840,50", "0,00", "ok"],
        ["Débito", "742,30", "742,30", "0,00", "ok"],
        ["PIX", "618,00", "618,00", "0,00", "ok"],
      ].map(([k, e, c, d, st], i) => (
        <div key={i} className="grid grid-cols-4 gap-2 text-[11px] py-2 border-t border-white/[0.04]">
          <span className="text-[#f1f1ef]">{k}</span>
          <span className="text-right text-[#a1a1aa] font-mono">{e}</span>
          <span className="text-right text-[#a1a1aa] font-mono">{c}</span>
          <span className={`text-right font-mono ${st === "ok" ? "text-emerald-400" : "text-amber-400"}`}>{d}</span>
        </div>
      ))}
      <div className="mt-4 pt-4 border-t border-white/[0.06] flex justify-between items-center">
        <span className="text-[#8b8b94] text-xs">Status</span>
        <span className="text-amber-400 text-xs">faltou R$ 2,00 · operador notificado</span>
      </div>
    </div>
  );
}

function TechDemo() {
  const decisions = [
    {
      q: "Compra chegou mais cara?",
      a: "O sistema identifica os itens da NF-e, atualiza custos e mostra quais produtos perderam margem.",
    },
    {
      q: "O caixa fechou diferente?",
      a: "A conferência aponta diferença por forma de pagamento para você corrigir antes de virar bagunça.",
    },
    {
      q: "A meta do dia está longe?",
      a: "A OPLYX compara venda, ritmo do turno, custos e histórico para mostrar quanto ainda precisa entrar.",
    },
    {
      q: "O produto vende muito, mas deixa pouca margem?",
      a: "Você enxerga quais itens puxam faturamento e quais realmente pagam a operação.",
    },
  ];
  return (
    <section id="demo" className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold mb-4">Demo técnica</div>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] leading-[1.05] max-w-3xl">
            Cada tela aqui{" "}
            <span className="italic text-[#23784c] font-light">é uma decisão que o dono precisa tomar.</span>
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-6 text-[#52524a] text-lg leading-relaxed max-w-2xl">
            A OPLYX mostra o que normalmente fica escondido entre caixa, estoque, nota fiscal e planilha.
          </p>
        </Reveal>

        {/* Mocks com capturas reais */}
        <div className="mt-14 grid lg:grid-cols-2 gap-5">
          <Reveal delay={300}><PurchaseInvoiceMock /></Reveal>
          <Reveal delay={400}><CashCloseMock /></Reveal>
        </div>

        {/* Cards de decisão */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {decisions.map((d, i) => (
            <Reveal key={i} delay={450 + i * 80}>
              <div className="bg-white border border-[#e5e3d8] rounded-2xl p-6 h-full">
                <div className="font-[Fraunces] text-xl text-[#0a0a0a] leading-snug">{d.q}</div>
                <p className="mt-3 text-sm text-[#52524a] leading-relaxed">{d.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section: FAQ ─────────────────────────────────────────────────
function Faq() {
  const items = [
    {
      q: "E se a internet cair no meio do serviço?",
      a: "O caixa continua vendendo. Comandas, vendas e emissão de NFC-e ficam em contingência e sincronizam sozinhas assim que a conexão volta, sem nenhuma intervenção sua.",
    },
    {
      q: "O OPLYX funciona offline de verdade?",
      a: "Sim. Se a internet cair, o sistema continua operando no browser, sem precisar instalar nada. Você vende, abre e fecha comandas, mantém o caixa funcionando. Quando a rede voltar, tudo sincroniza automaticamente. Isso inclui NFC-e em contingência: as notas ficam na fila e são transmitidas quando a conexão volta.",
    },
    {
      q: "O que diferencia o OPLYX dos outros sistemas?",
      a: "A maioria dos sistemas registra o que já aconteceu. Aqui você vê o problema se formar antes dele aparecer no DRE.",
    },
    {
      q: "Minha equipe não é tech. Vão conseguir usar?",
      a: "Sim. O PDV foi desenhado para funcionar com tela travada no celular: garçom abre comanda, lança item, fecha. Sem menus escondidos, sem treinamento longo. Os primeiros 7 dias têm acompanhamento ativo da nossa equipe: importação de produtos, configuração fiscal e treino do time de salão e cozinha.",
    },
    {
      q: "Quanto tempo leva para implantar e ver resultado?",
      a: "O cliente médio entra em operação em 3 a 5 dias, com cadastro de produtos, certificado digital e configuração fiscal inclusos. O DRE começa a fechar no primeiro mês completo. Quem vem de outro sistema leva em média 5 a 9 dias para migrar.",
    },
    {
      q: "Preciso de contador para emitir NFC-e pela OPLYX?",
      a: (
        <>
          <p>Para emitir pelo sistema, não. A emissão fiscal é integrada ao PDV: você configura o certificado digital uma vez e a NFC-e sai a cada venda. Em contingência, o sistema emite offline e transmite quando a conexão volta.</p>
          <p>O que continua sendo responsabilidade da empresa: estar regularizada e ter a classificação tributária dos produtos correta. A OPLYX sugere a classificação de cada item com a fundamentação legal visível na tela, e você ou seu contador confirmam antes de começar a emitir. Dúvida tributária deve ser validada com seu contador.</p>
        </>
      ),
    },
    {
      q: "Já uso o Colibri / Mensuall / iFood POS. Como fico?",
      a: "Migramos com um agente de onboarding: você envia os arquivos do seu sistema atual (produtos, clientes, XML de compra, até ficha técnica em foto) e o agente organiza tudo, casa com o que já existe no cadastro e separa o que ficou incerto para você revisar. Nada entra sozinho. O que não migra: notas fiscais emitidas e fechamentos de caixa anteriores. Esses ficam no seu sistema antigo como arquivo. O cliente médio conclui a migração e está operando em 5 a 9 dias. A migração está inclusa no onboarding de ambos os planos.",
    },
    {
      q: "E se eu já uso outro sistema?",
      a: "Não precisa ser um dos sistemas citados acima. Importamos de qualquer um que exporte CSV, SPED ou XML: produtos, clientes, contas a pagar, histórico de vendas. O cliente médio sai do sistema antigo em 5 a 9 dias.",
    },
    {
      q: "Por que a OPLYX custa mais que um PDV tradicional?",
      a: "Porque a OPLYX não é só um caixa com nota fiscal. Um PDV tradicional registra a venda; aqui, o mesmo preço cobre estoque, CMV, financeiro, equipe e DRE. A diferença se paga reduzindo retrabalho, erro de estoque, perda de margem, fechamento manual e decisão tomada no escuro. Se você só precisa emitir NFC-e e passar venda no caixa, um PDV simples pode ser suficiente.",
    },
    {
      q: "A OPLYX é para operação pequena?",
      a: "Sim, desde que ela já tenha complexidade real. Se você tem caixa, equipe, compras recorrentes, produtos com custo variável, venda presencial e precisa emitir nota, a OPLYX faz sentido mesmo em uma operação pequena. O foco aqui é tirar do improviso o que hoje o dono só controla no olho, sem tentar parecer um sistema de operação grande.",
    },
    {
      q: "Posso confiar minha operação em um sistema novo?",
      a: "A OPLYX é nova no mercado, mas não nasceu em laboratório. Foi criada dentro de uma operação real de foodservice, por quem vive caixa, fornecedor, estoque, fechamento e rotina de loja desde 2016. Por isso a implantação é acompanhada de perto: configuramos produtos, fiscal, equipe e rotina junto com você antes de colocar o sistema no centro da operação. Sem fidelidade: se não fizer sentido, você cancela, exporta seus dados e segue a vida.",
    },
    {
      q: "Como funciona a contratação?",
      a: "Você cria a conta, escolhe o plano e cadastra a forma de pagamento: cartão de crédito, Pix ou boleto. O acesso é liberado na hora e nada é cobrado nesse dia. A primeira mensalidade vence 5 dias depois; as seguintes caem sempre no mesmo dia do mês (5, 10, 15 ou 20). As datas exatas aparecem na tela antes de você confirmar. Nos primeiros dias, ajudamos você a configurar o essencial: produtos, fiscal, formas de pagamento, equipe e rotina de venda. Sem fidelidade e sem multa de cancelamento. Você pode pedir o encerramento a qualquer momento, com aviso prévio de 30 dias. Durante esse período, o serviço e as mensalidades continuam normalmente. Antes de confirmar, mostramos as datas de encerramento, as cobranças restantes e o prazo para exportar seus dados.",
    },
    {
      q: "Quem me atende se eu tiver problema?",
      a: "O onboarding, a migração e o treinamento são humanos. Você fala com quem conhece o produto e entende operação de foodservice. Nos primeiros dias, acompanhamos a implantação de perto para garantir que caixa, fiscal, produtos, equipe e rotina estejam funcionando antes de você depender da OPLYX no serviço.",
    },
    {
      q: "O plano Full tem limite de notas fiscais?",
      a: "Não. No Full, NFC-e, NF-e e NF-e de compra importadas são ilimitadas, sem cobrança por excedente. O limite mensal com preço por documento excedente existe só no Basic.",
    },
    {
      q: "Preciso trocar tudo de uma vez?",
      a: "Não. A implantação começa pelo essencial: produtos, fiscal, caixa e operação de venda. Depois ativamos estoque, ficha técnica, financeiro, DRE e recursos avançados conforme sua equipe ganha segurança. A ideia é migrar com controle, sem travar sua operação para trocar de sistema.",
    },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-32">
      <div className="max-w-4xl mx-auto px-6">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold mb-4">Dúvidas frequentes</div>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl tracking-tight text-[#0a0a0a] leading-[1.05]">
            O que importa, antes de você tentar.
          </h2>
        </Reveal>
        <div className="mt-14 divide-y divide-[#e5e3d8] border-y border-[#e5e3d8]">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <button
                key={i}
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                className="w-full text-left py-7 group"
                data-testid={`faq-${i}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <span className="font-[Fraunces] text-xl sm:text-2xl text-[#0a0a0a] leading-tight">{it.q}</span>
                  <span aria-hidden="true" className={`text-[#23784c] text-2xl flex-shrink-0 mt-1 transition-transform ${isOpen ? "rotate-45" : ""}`}>+</span>
                </div>
                <div className="grid transition-all duration-500" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                  <div className="overflow-hidden">
                    {typeof it.a === "string" ? (
                      <p className="text-[#52524a] leading-relaxed mt-4 max-w-2xl">{it.a}</p>
                    ) : (
                      <div className="text-[#52524a] leading-relaxed mt-4 max-w-2xl space-y-4">{it.a}</div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Section: Pricing ─────────────────────────────────────────────
function CheckIcon({ className = "" }) {
  return (
    <svg className={`w-3.5 h-3.5 flex-shrink-0 ${className}`} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4L8.5 12l6.8-6.7a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
    </svg>
  );
}

function fmtBRL(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

function SparkleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 2a.75.75 0 0 1 .74.627l.607 3.646 3.646.607a.75.75 0 0 1 0 1.48l-3.646.607-.607 3.646a.75.75 0 0 1-1.48 0l-.607-3.646-3.646-.607a.75.75 0 0 1 0-1.48l3.646-.607.607-3.646A.75.75 0 0 1 10 2Z" />
    </svg>
  );
}

const INTELLIGENCE_LABEL = "OPLYX Intelligence · copiloto de gestão com IA";

function PlanCard({ planKey, kind, price, headline, description, fiscalQuota, overage, extraBranch, features, ctaLabel, onCta, badge, dark = false, hasIntelligence = false, testid }) {
  return (
    <div
      className={`relative rounded-2xl p-8 sm:p-10 transition-all duration-500 hover:-translate-y-0.5 ${
        dark
          ? "bg-[#0a0a0a] text-[#f1f1ef] border border-[#0a0a0a] shadow-[0_30px_80px_-30px_rgba(10,10,10,0.5)]"
          : "bg-white text-[#0a0a0a] border border-[#e5e3d8] shadow-[0_20px_50px_-30px_rgba(10,10,10,0.12)]"
      }`}
      data-testid={testid}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#23784c] text-white text-[11px] uppercase tracking-[0.2em] font-semibold rounded-full px-4 py-1.5 whitespace-nowrap">
          {badge}
        </div>
      )}
      <div className={`text-[10px] uppercase tracking-[0.28em] font-semibold ${dark ? "text-[#a1a1aa]" : "text-[#6b6b62]"}`}>{kind}</div>
      <div className={`mt-6 font-[Fraunces] text-6xl sm:text-7xl tracking-[-0.02em] leading-none ${dark ? "text-[#f1f1ef]" : "text-[#0a0a0a]"}`}>
        {fmtBRL(price)}
      </div>
      <div className={`mt-5 text-sm ${dark ? "text-[#a1a1aa]" : "text-[#52524a]"}`}>por mês · sem fidelidade · cancelamento com aviso de 30 dias</div>
      {description && (
        <p className={`mt-4 text-sm leading-relaxed ${dark ? "text-[#a1a1aa]" : "text-[#52524a]"}`}>{description}</p>
      )}

      <div className={`mt-6 rounded-xl p-4 border ${dark ? "bg-white/[0.04] border-white/[0.08]" : "bg-[#f5f4ee] border-[#e5e3d8]"}`} data-testid={`${testid}-fiscal-quota`}>
        {fiscalQuota > 0 ? (
          <>
            <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${dark ? "text-[#4BB87A]" : "text-[#23784c]"}`}>Franquia fiscal incluída</div>
            <div className={`mt-1.5 font-[Fraunces] text-2xl ${dark ? "text-[#f1f1ef]" : "text-[#0a0a0a]"}`}>
              {fiscalQuota.toLocaleString("pt-BR")}
              <span className={`text-sm font-sans ml-1.5 ${dark ? "text-[#a1a1aa]" : "text-[#52524a]"}`}>documentos / mês</span>
            </div>
          </>
        ) : (
          <>
            <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold ${dark ? "text-[#4BB87A]" : "text-[#23784c]"}`}>
              <CheckIcon className={dark ? "text-[#4BB87A]" : "text-[#23784c]"} />
              Franquia fiscal ilimitada
            </div>
            <div className={`mt-1.5 font-[Fraunces] text-2xl ${dark ? "text-[#f1f1ef]" : "text-[#0a0a0a]"}`}>
              Sem limite
              <span className={`text-sm font-sans ml-1.5 ${dark ? "text-[#a1a1aa]" : "text-[#52524a]"}`}>de documentos / mês</span>
            </div>
          </>
        )}
        <div className={`mt-1 text-[11px] leading-snug ${dark ? "text-[#a1a1aa]" : "text-[#52524a]"}`}>NFC-e + NF-e emitidas + NF-e de compra importadas{fiscalQuota > 0 ? "" : ", sem cobrança por excedente"}</div>
        {overage > 0 && (
          <div className={`mt-3 pt-3 border-t flex items-baseline justify-between ${dark ? "border-white/10" : "border-[#e5e3d8]"}`}>
            <span className={`text-[11px] ${dark ? "text-[#a1a1aa]" : "text-[#52524a]"}`}>Excedente</span>
            <span className={`font-mono text-sm font-semibold ${dark ? "text-[#d4a04c]" : "text-[#9a5a29]"}`}>{fmtBRL(overage)} / doc</span>
          </div>
        )}
        {extraBranch > 0 && (
          <div className="mt-2">
            <div className="flex items-baseline justify-between">
              <span className={`text-[11px] ${dark ? "text-[#a1a1aa]" : "text-[#52524a]"}`}>Filial adicional</span>
              <span className={`font-mono text-sm font-semibold ${dark ? "text-[#d4a04c]" : "text-[#9a5a29]"}`}>{fmtBRL(extraBranch)} / mês</span>
            </div>
            <p className={`text-[11px] mt-1.5 leading-snug ${dark ? "text-[#8b8b94]" : "text-[#6b6b62]"}`}>
              Cada unidade adicional vem com estoque, caixa, equipe e DRE próprios. Sem criar outra conta, sem trocar de sistema.
            </p>
          </div>
        )}
      </div>

      <div className={`my-7 h-px ${dark ? "bg-white/10" : "bg-[#e5e3d8]"}`} />
      <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${dark ? "text-[#a1a1aa]" : "text-[#6b6b62]"} mb-5`}>{headline}</div>

      {hasIntelligence && dark && (
        <div className="mb-5 rounded-xl border border-[#b66a32]/30 bg-[#b66a32]/[0.07] p-4" data-testid={`${testid}-intelligence`}>
          <div className="flex items-center gap-2 text-[#d4a04c]">
            <SparkleIcon />
            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold">OPLYX Intelligence</span>
          </div>
          <p className="mt-2 text-sm text-[#f1f1ef] leading-snug">Copiloto de gestão que entende todos os dados da sua operação</p>
        </div>
      )}

      <ul className="space-y-3.5">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] leading-snug">
            <span className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full ${dark ? "bg-[#2A8A58]/15 text-[#4BB87A]" : "bg-[#2A8A58]/10 text-[#23784c]"}`}>
              <CheckIcon />
            </span>
            <span className={dark ? "text-[#f1f1ef]" : "text-[#0a0a0a]"}>{f}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => onCta(planKey)}
        className={`mt-10 w-full rounded-full font-semibold py-4 transition-all hover:-translate-y-0.5 ${
          dark
            ? "bg-white hover:bg-[#f5f4ee] text-[#0a0a0a]"
            : "border border-[#0a0a0a]/15 hover:border-[#0a0a0a]/40 text-[#0a0a0a]"
        }`}
        data-testid={`${testid}-cta`}
      >
        {ctaLabel}
      </button>
      <div className={`mt-5 text-center text-[12px] ${dark ? "text-[#8b8b94]" : "text-[#6b6b62]"}`}>
        Hoje você não paga nada · 1ª mensalidade em 5 dias, depois todo mês no mesmo dia · sem fidelidade
      </div>
    </div>
  );
}

function Pricing({ onCta }) {
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/plans`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => setPlans(Array.isArray(data) ? data : []))
      .catch(() => setError(true));
  }, []);
  const byKey = (k) => (plans || []).find((p) => p.key === k);
  const basic = byKey("basic");
  const full = byKey("full");
  // "A partir de": o menor plano da tabela (admin). 389 só se a API falhar.
  const minMonth = Math.min(...(plans || []).map((p) => Number(p.month) || Infinity), Infinity);
  const aPartirDe = Number.isFinite(minMonth) ? minMonth : 389;
  return (
    <section id="precos" className="py-32 relative">
      <div className="max-w-6xl mx-auto px-6">
        {/* Texto ponte 15a */}
        <Reveal>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-lg text-[#52524a] leading-relaxed">
              Antes de comparar preço, compare o que sai da sua rotina.
            </p>
            <p className="mt-5 text-[#52524a] leading-relaxed">
              A OPLYX custa mais que um PDV simples porque o problema que ela resolve não para no caixa. Ela substitui pedaços que normalmente ficam espalhados entre sistema de venda,
              planilha de estoque, controle manual de CMV, emissão fiscal, conferência de caixa, financeiro, meta do dia e relatório de fechamento.
            </p>
            <p className="mt-5 text-[#52524a] leading-relaxed">
              Se todo mês você perde tempo fechando número, corrigindo estoque, calculando custo, conferindo venda e tentando entender se a operação deu lucro,
              o custo real do sistema barato não está na mensalidade, e sim no retrabalho e na margem que escapa.
            </p>
            <p className="mt-6 text-[#0a0a0a] font-medium">
              Planos a partir de R$ {aPartirDe.toLocaleString("pt-BR")}/mês, com implantação, migração assistida e treinamento incluídos.
            </p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#23784c] font-semibold mb-4 text-center">Planos</div>
        </Reveal>
        <Reveal delay={150}>
          <h2 className="font-[Fraunces] text-4xl sm:text-5xl lg:text-6xl tracking-[-0.01em] text-[#0a0a0a] leading-[1.05] text-center max-w-3xl mx-auto">
            Dois caminhos.{" "}
            <span className="italic text-[#23784c] font-light">Mesmo nível de seriedade.</span>
          </h2>
        </Reveal>
        <Reveal delay={220}>
          <p className="mt-6 text-[#52524a] text-lg leading-relaxed text-center max-w-2xl mx-auto">
            Sem fidelidade e sem multa de cancelamento. Você pode pedir o encerramento a qualquer momento, com aviso prévio de 30 dias. Durante esse período, o serviço e as mensalidades continuam normalmente. Antes de confirmar, mostramos as datas de encerramento, as cobranças restantes e o prazo para exportar seus dados.
          </p>
        </Reveal>
        {/* Fallback estático — preços reais servidos no HTML para crawlers e falha de JS.
            Mantenha sincronizado com o texto "a partir de" acima e com BIBLIA_COMERCIAL.md
            sempre que os planos mudarem de preço — não é alimentado pela API. */}
        {error && (
          <div className="mt-12 max-w-2xl mx-auto border border-[#e5e3d8] rounded-2xl overflow-hidden" data-testid="plans-static-fallback">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#e5e3d8]">
              <div className="p-8">
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#6b6b62]">Basic</div>
                <div className="font-[Fraunces] text-5xl text-[#0a0a0a] mt-4">R$ 389</div>
                <div className="text-sm text-[#52524a] mt-2">por mês · sem fidelidade · cancelamento com aviso de 30 dias</div>
                <p className="mt-4 text-sm text-[#52524a] leading-relaxed">Para operações que querem trocar o PDV tradicional por uma base única de venda, fiscal, financeiro e controle operacional.</p>
              </div>
              <div className="p-8 bg-[#0a0a0a] text-[#f1f1ef]">
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#a1a1aa]">Full</div>
                <div className="font-[Fraunces] text-5xl text-[#f1f1ef] mt-4">R$ 489</div>
                <div className="text-sm text-[#a1a1aa] mt-2">por mês · sem fidelidade · cancelamento com aviso de 30 dias</div>
                <p className="mt-4 text-sm text-[#a1a1aa] leading-relaxed">Para operações que querem ir além do caixa e controlar margem, cozinha, DRE, cliente e decisão operacional em tempo real.</p>
                <p className="mt-3 text-xs text-[#4BB87A]">Documentos fiscais ilimitados, sem cobrança por excedente.</p>
                <p className="mt-1.5 text-xs text-[#8b8b94]">Filial adicional: R$ 199/mês. Estoque, caixa, equipe e DRE próprios por unidade.</p>
              </div>
            </div>
            <div className="px-8 py-4 bg-[#f5f4ee] text-sm text-[#52524a] text-center border-t border-[#e5e3d8]">
              Hoje você não paga nada · 1ª mensalidade em 5 dias, depois todo mês no mesmo dia · sem fidelidade · entre em contato: <a href="mailto:contato@oplyx.com.br" className="text-[#23784c] underline">contato@oplyx.com.br</a>
            </div>
          </div>
        )}
        {!plans && !error && (
          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {[0, 1].map((i) => <div key={i} className="rounded-2xl bg-white border border-[#e5e3d8] p-10 animate-pulse h-[680px]" />)}
          </div>
        )}
        {plans && basic && full && (
          <div className="mt-16 grid md:grid-cols-2 gap-6 items-start">
            <Reveal delay={300}>
              <PlanCard testid="plan-basic" planKey={basic.key} kind="Basic" price={basic.month} headline="Operação completa" description="Para operações que querem trocar o PDV tradicional por uma base única de venda, fiscal, financeiro e controle operacional." fiscalQuota={basic.doc_limit_month} overage={basic.doc_overage_price} extraBranch={basic.extra_branch_month} ctaLabel="Começar com Basic" onCta={onCta} features={planLandingBullets(basic.features)} />
            </Reveal>
            <Reveal delay={400}>
              <PlanCard testid="plan-full" planKey={full.key} kind="Full" price={full.month} dark badge="Mais completo" headline="Tudo do Basic, mais:" description="Para operações que querem ir além do caixa e controlar margem, cozinha, DRE, cliente e decisão operacional em tempo real." fiscalQuota={full.doc_limit_month} overage={full.doc_overage_price} extraBranch={full.extra_branch_month} ctaLabel="Começar com Full" onCta={onCta} features={planLandingBullets(full.features).filter((f) => !planLandingBullets(basic.features).includes(f) && f !== INTELLIGENCE_LABEL)} hasIntelligence={!!full.features?.intelligence} />
            </Reveal>
          </div>
        )}
        <Reveal delay={500}>
          <p className="mt-12 text-center text-[13px] text-[#6b6b62]">
            Setup, importação de dados e treinamento da equipe inclusos em ambos os planos.{" "}
            <a href="#agendar" className="text-[#23784c] hover:text-[#185c3a] underline underline-offset-4">Conversar antes de contratar →</a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Section: Final CTA ───────────────────────────────────────────
function FinalCta({ onPrimary }) {
  return (
    <section id="agendar" className="py-32 relative overflow-hidden bg-[#0a0a0a] text-[#f1f1ef]">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[600px] rounded-full bg-[#2A8A58]/[0.12] blur-3xl" />
      </div>
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="font-[Fraunces] text-5xl sm:text-6xl lg:text-7xl tracking-[-0.02em] leading-[0.95] text-[#f1f1ef]">
            O que você gerencia hoje no olho{" "}
            <span className="italic text-[#4BB87A] font-light">começa a aparecer no DRE.</span>
          </h2>
        </Reveal>
        <Reveal delay={150}>
          <p className="mt-8 text-lg text-[#a1a1aa] max-w-xl mx-auto leading-relaxed">
            Caixa, fiscal, estoque, custo, financeiro e decisão no mesmo lugar, com a implantação feita junto com você.
          </p>
        </Reveal>
        <Reveal delay={220}>
          <p className="mt-5 text-base text-[#8b8b94] max-w-xl mx-auto leading-relaxed">
            Sem fidelidade e sem multa: se não fizer sentido para sua operação, você pede o encerramento com 30 dias de aviso (as mensalidades seguem durante o aviso) e leva seus dados.
          </p>
        </Reveal>
        <Reveal delay={300}>
          <div className="mt-12 flex flex-wrap gap-3 justify-center">
            <button onClick={onPrimary} className="bg-[#23784c] hover:bg-[#4BB87A] text-white font-semibold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5 shadow-[0_10px_40px_-10px_rgba(42,138,88,0.6)]" data-testid="final-cta-trial">
              Contratar a OPLYX
            </button>

            <a
              href="https://wa.me/5548991207631?text=Ol%C3%A1%2C%20quero%20agendar%2015%20minutos%20para%20conhecer%20o%20OPLYX"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white/25 hover:border-white/60 text-[#f1f1ef] px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
              data-testid="final-cta-schedule"
            >
              Agendar 15 min com a gente
            </a>
          </div>
          <div className="mt-6 text-[12px] text-[#8b8b94]">Onboarding humano · Sem fidelidade · Migração inclusa</div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="border-t border-[#e5e3d8] py-12 bg-[#f5f4ee]">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8 items-start">
        <div>
          <img src="/oplyx-small-cream.png" alt="OPLYX" height={24} style={{ height: 24, width: "auto", objectFit: "contain" }} />
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#23784c] mt-1">foodservice OS</div>
          <p className="text-xs text-[#52524a] mt-5 leading-relaxed max-w-xs">
            Plataforma operacional completa para cafeterias e restaurantes que crescem com controle.
          </p>
        </div>
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b62] mb-4">Produto</div>
          <ul className="space-y-2 text-[#52524a]">
            <li><a href="#recusteio" className="hover:text-[#0a0a0a]">Recusteio automático</a></li>
            <li><a href="#pilares" className="hover:text-[#0a0a0a]">Como funciona</a></li>
            <li><a href="#migracao" className="hover:text-[#0a0a0a]">Migração</a></li>
            <li><a href="#central-financeira" className="hover:text-[#0a0a0a]">Central Financeira</a></li>
            <li><a href="#diferenciais" className="hover:text-[#0a0a0a]">Comparativo</a></li>
            <li><a href="#origem" className="hover:text-[#0a0a0a]">Origem</a></li>
            <li><a href="#demo" className="hover:text-[#0a0a0a]">Demo</a></li>
            <li><a href="#precos" className="hover:text-[#0a0a0a]">Planos</a></li>
            <li><a href="#faq" className="hover:text-[#0a0a0a]">FAQ</a></li>
          </ul>
        </div>
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b62] mb-4">Conversar</div>
          <ul className="space-y-2 text-[#52524a]">
            <li>
              <Link to="/auth" onClick={(e) => { if (typeof window !== "undefined") { const h = (window.location.hostname || "").toLowerCase(); if (h === "oplyx.com.br" || h === "www.oplyx.com.br") { e.preventDefault(); window.location.href = "https://app.oplyx.com.br/auth"; } } }} className="hover:text-[#0a0a0a]">Entrar</Link>
            </li>
            <li><a href="mailto:contato@oplyx.com.br" className="hover:text-[#0a0a0a]">contato@oplyx.com.br</a></li>

            <li>
              <a href="https://wa.me/5548991207631" target="_blank" rel="noopener noreferrer" className="hover:text-[#0a0a0a] flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-[#23784c]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
                WhatsApp
              </a>
            </li>
          </ul>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b62] mt-6 mb-4">Legal</div>
          <ul className="space-y-2 text-[#52524a]">
            <li><Link to="/termos" className="hover:text-[#0a0a0a]">Termos de uso</Link></li>
            <li><Link to="/privacidade" className="hover:text-[#0a0a0a]">Política de privacidade</Link></li>
            <li>
              <button type="button" onClick={openCookiePreferences} className="hover:text-[#0a0a0a] text-left" data-testid="footer-cookie-preferences">
                Preferências de cookies
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-12 pt-6 border-t border-[#e5e3d8] flex flex-wrap items-center justify-between gap-3 text-[11px] text-[#6b6b62]">
        <span data-testid="footer-company">
          © {new Date().getFullYear()} {COMPANY.razaoSocial} · CNPJ {COMPANY.cnpj} · {companyAddress()}
        </span>
        <div className="flex items-center gap-3">
          <img
            src="/programa-nascer-badge.png"
            alt="Programa Nascer: FAPESC, SCTI e SEBRAE"
            className="h-9 w-auto rounded-sm"
            style={{ objectFit: "contain" }}
          />
          <img
            src="/stone-official-partner-badge.png"
            alt="Stone - Official Partner"
            className="h-9 w-auto"
            style={{ objectFit: "contain" }}
          />
        </div>
      </div>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────────
function _isApex() {
  if (typeof window === "undefined") return false;
  const h = (window.location.hostname || "").toLowerCase();
  return h === "oplyx.com.br" || h === "www.oplyx.com.br";
}
const APP_HOST = "https://app.oplyx.com.br";

// Indicação: quem chega pela assinatura "Gestão por OPLYX" de um estabelecimento
// traz ?ref=<código>&src=<ponto>. Guarda por 60 dias neste navegador (a pessoa
// pode voltar outro dia) e leva junto para o cadastro.
const REF_KEY = "oplyx_ref";
const REF_TTL_MS = 60 * 24 * 3600 * 1000;
function lerIndicacao() {
  try {
    const r = JSON.parse(localStorage.getItem(REF_KEY) || "null");
    return r && Date.now() - r.at < REF_TTL_MS ? r : null;
  } catch {
    return null;
  }
}
function capturarIndicacao() {
  const p = new URLSearchParams(window.location.search);
  const ref = (p.get("ref") || "").toLowerCase();
  if (!/^[a-z0-9]{6,12}$/.test(ref)) return;
  const src = (p.get("src") || "").toLowerCase();
  try { localStorage.setItem(REF_KEY, JSON.stringify({ ref, src, at: Date.now() })); } catch { /* sem storage */ }
  const marca = `oplyx_ref_visit_${ref}_${src}`;
  try {
    if (sessionStorage.getItem(marca)) return;
    sessionStorage.setItem(marca, "1");
  } catch { /* sem storage */ }
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/ref-visit`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ref, src }),
  }).catch(() => {});
}

export default function Landing() {
  const nav = useNavigate();
  useEffect(() => { capturarIndicacao(); }, []);
  // `plan` vem só dos cards de preço; os outros CTAs chamam com o evento do clique.
  const goSignup = (plan) => {
    const p = new URLSearchParams({ mode: "signup" });
    if (typeof plan === "string" && plan) p.set("plan", plan);
    const ind = lerIndicacao();
    if (ind) { p.set("ref", ind.ref); if (ind.src) p.set("ref_src", ind.src); }
    const qs = `?${p.toString()}`;
    if (_isApex()) {
      window.location.href = `${APP_HOST}/auth${qs}`;
    } else {
      nav(`/auth${qs}`);
    }
  };
  return (
    <div className="min-h-screen bg-[#f5f4ee] text-[#0a0a0a] antialiased selection:bg-[#2A8A58]/30 selection:text-[#0a0a0a]">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:bg-white focus:text-[#0a0a0a] focus:px-4 focus:py-2 focus:rounded-full focus:shadow-lg"
      >
        Pular para o conteúdo
      </a>
      <TopNav onCta={goSignup} />
      <main id="conteudo" tabIndex={-1}>
        <Hero onPrimary={goSignup} />
        <PdvBlock />
        <ProblemSection />
        <ConsumptionShowcase />
        <Manifesto />
        <Pillars />
        <OnboardingAgentSection />
        <IntelligenceSection />
        <CentralFinanceiraSection />
        <ComparisonSection />
        <PriceJustification />
        <OfflineSection />
        <TechDemo />
        <OriginStory />
        <Pricing onCta={goSignup} />
        <Faq />
        <FinalCta onPrimary={goSignup} />
      </main>
      <Footer />
    </div>
  );
}
